import { Role } from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { B2BContextProps } from './taxonomy';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NCT_RE = /^nct\d{8}$/i;
const ID_PLACEHOLDER = ':id';
const APP_PREFIX = 'app';
// Sections where the first id in the path is a trial. Elsewhere ids are teams, surveys,
// questions or patients and must not leave the browser. Adding a section here is a privacy
// decision: its first uuid is then stored in the analytics lake as trialId.
const TRIAL_SECTIONS = new Set([
  'trial-recruitment',
  'pipeline',
  'trials-library',
  'trials-redesign',
]);

const isId = (segment: string): boolean => UUID_RE.test(segment) || NCT_RE.test(segment);

export type B2BRouteContext = Pick<
  B2BContextProps,
  'section' | 'page' | 'tab' | 'subTab' | 'trialId'
>;

/**
 * Breaks a B2B pathname into section / page / tab / subTab with every id masked as `:id`.
 *   /app/trial-recruitment/trials/<uuid>/criterias → section trial-recruitment,
 *     page trial-recruitment/trials/:id/criterias, tab criterias, trialId <uuid>
 *   /app/team/<uuid>/users → section team, page team/:id/users, tab users
 *   /app/surveys/create → section surveys, page surveys/create, no tab
 * `tab` is the segment after the last id, `subTab` the one after that.
 */
export const parseB2BRoute = (pathname: string): B2BRouteContext => {
  const raw = pathname.split('/').filter(Boolean);
  const segments = raw[0] === APP_PREFIX ? raw.slice(1) : raw;
  if (segments.length === 0) return {};

  const [section, ...rest] = segments;
  const masked = rest.map((segment) => (isId(segment) ? ID_PLACEHOLDER : segment.toLowerCase()));
  const lastIdIndex = masked.lastIndexOf(ID_PLACEHOLDER);
  const afterId = lastIdIndex === -1 ? [] : masked.slice(lastIdIndex + 1);
  const firstId = rest.find(isId);
  const trialId =
    TRIAL_SECTIONS.has(section) && firstId && UUID_RE.test(firstId) ? firstId : undefined;

  return {
    section,
    page: [section, ...masked].join('/'),
    tab: afterId[0],
    subTab: afterId[1],
    trialId,
  };
};

export const currentB2BRoute = (): B2BRouteContext =>
  typeof window === 'undefined' ? {} : parseB2BRoute(window.location.pathname);

export type B2BAttribution = Pick<B2BContextProps, 'userRole' | 'teamId'>;

/**
 * Who the signed-in user is acting as right now, or undefined for a patient or when nobody is
 * signed in. Client-reported: the Pulse endpoint does not verify it.
 */
export const b2bAttribution = (): B2BAttribution | undefined => {
  const { user } = useAppUserStore.getState();
  const userRole = user?.currentRole;
  if (!userRole || userRole === Role.Volunteer) return undefined;
  const teamId = user?.teamId ?? undefined;
  return { userRole, ...(teamId ? { teamId } : {}) };
};

/**
 * Stamps role and team on a B2B event, or returns undefined when the event must not be sent.
 * An explicit `userRole` means the caller carries its own complete attribution — a page event
 * replaying the snapshot taken when the page opened, or a login firing before the store updates
 * — so the team comes from that same snapshot. Reading the store for the team would let a page
 * opened before team selection emit its view with no team and its leave under the new one.
 */
export const withB2BContext = <T extends B2BContextProps>(props: T): T | undefined => {
  const attribution = props.userRole
    ? { userRole: props.userRole, teamId: props.teamId }
    : b2bAttribution();
  const userRole = attribution?.userRole;
  if (!userRole || userRole === Role.Volunteer) return undefined;
  const teamId = attribution?.teamId;
  return { ...props, userRole, ...(teamId ? { teamId } : {}) };
};
